import { useState } from "react";
import PersonForm from "./PersonForm";
import { useSelector, useDispatch } from "react-redux";
import {
  buyersSelector,
  sellersSelector,
  idSelector,
  addParty,
  removeParty,
} from "../../../features/cartransferSlice";
import { Row, Col, Button, Popover, Tooltip, Badge } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  BankOutlined,
  CaretDownOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";

// ── Helpers ─────────────────────────────────────────────────────────
const getInitials = (person) => {
  const n = (person?.name || "").charAt(0);
  const l = (person?.firstLastName || "").charAt(0);
  return (n + l).toUpperCase() || "·";
};

const getDisplayName = (person) => {
  if (!person) return null;
  if (person.representsCompany && person.companyName) return person.companyName;
  const parts = [person.name, person.firstLastName, person.secondLastName].filter(Boolean);
  return parts.length ? parts.join(" ") : null;
};

const completionScore = (person) => {
  if (!person) return 0;
  const required = ["rut", "name", "firstLastName", "civilStatus", "phone", "address", "commune"];
  const filled = required.filter((k) => person[k] && String(person[k]).trim() !== "").length;
  return Math.round((filled / required.length) * 100);
};

// ── Collapsed row ────────────────────────────────────────────────────
const PartyRow = ({ person, index, label, role, color, accentColor, isCollapsed, onToggle, onRemove, canRemove }) => {
  const name = getDisplayName(person);
  const initials = getInitials(person);
  const isCompany = !!person?.representsCompany;
  const pct = completionScore(person);

  const avatarBg = isCollapsed
    ? accentColor + "22"
    : accentColor + "15";

  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        background: isCollapsed ? "#fafafa" : "#fff",
        border: `1px solid ${isCollapsed ? "#e8e8e8" : accentColor + "55"}`,
        borderRadius: isCollapsed ? 10 : "10px 10px 0 0",
        borderBottom: isCollapsed ? undefined : "none",
        cursor: "pointer",
        userSelect: "none",
        transition: "all 0.15s ease",
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          background: avatarBg,
          border: `2px solid ${accentColor}44`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 12,
          fontWeight: 700,
          color: accentColor,
          flexShrink: 0,
          letterSpacing: 0.5,
        }}
      >
        {isCompany ? <BankOutlined style={{ fontSize: 14 }} /> : initials}
      </div>

      {/* Info */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: "#1a1a1a" }}>
            {label}
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              borderRadius: 4,
              padding: "1px 6px",
              color: isCompany ? "#7c3aed" : "#0369a1",
              background: isCompany ? "#f3e8ff" : "#e0f2fe",
            }}
          >
            {isCompany ? "Empresa" : "Persona natural"}
          </span>
        </div>
        {name ? (
          <div
            style={{
              fontSize: 12,
              color: "#555",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
            {person?.rut && (
              <span style={{ color: "#999", marginLeft: 8 }}>{person.rut}</span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: 12, color: "#bbb", fontStyle: "italic" }}>
            Sin datos ingresados
          </div>
        )}
      </div>

      {/* Completion dots */}
      <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
        {[0, 25, 50, 75, 100].map((threshold) => (
          <div
            key={threshold}
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: pct >= threshold + 1 ? accentColor : "#e0e0e0",
            }}
          />
        ))}
      </div>

      {/* Actions */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {canRemove && (
          <Tooltip title="Eliminar">
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={onRemove}
              style={{ opacity: 0.7 }}
            />
          </Tooltip>
        )}
        <div
          style={{
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#aaa",
            fontSize: 10,
          }}
          onClick={onToggle}
        >
          {isCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
        </div>
      </div>
    </div>
  );
};

// ── Column (Vendedores / Compradores) ────────────────────────────────
const COLORS = {
  sellers: { accent: "#e65c00", light: "#fff7f0", border: "#ffd8b3", label: "Vendedor" },
  buyers:  { accent: "#1565c0", light: "#f0f5ff", border: "#bdd3f5", label: "Comprador" },
};

const PartyColumn = ({ role }) => {
  const dispatch = useDispatch();
  const parties = useSelector(role === "sellers" ? sellersSelector : buyersSelector);
  const cartransferId = useSelector(idSelector);
  const { accent, light, border, label } = COLORS[role];

  const [collapsed, setCollapsed] = useState({});
  const [popoverOpen, setPopoverOpen] = useState(false);

  const handleAddParty = (representsCompany) => {
    const newCollapsed = {};
    parties.forEach((_, i) => { newCollapsed[i] = true; });
    setCollapsed(newCollapsed);
    dispatch(addParty({ role, initialData: { representsCompany } }));
    setPopoverOpen(false);
  };

  const handleRemove = (index) => {
    dispatch(removeParty({ role, index }));
    setCollapsed((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      });
      return next;
    });
  };

  const toggleCollapse = (index) => {
    setCollapsed((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const addContent = (
    <div style={{ padding: "4px 2px", minWidth: 176 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          textTransform: "uppercase",
          color: "#888",
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        Tipo de {label.toLowerCase()}
      </div>
      <Button
        block
        style={{ marginBottom: 6, textAlign: "left", height: 38 }}
        icon={<UserOutlined style={{ color: "#555" }} />}
        onClick={() => handleAddParty(false)}
      >
        Persona natural
      </Button>
      <Button
        block
        style={{ textAlign: "left", height: 38 }}
        icon={<BankOutlined style={{ color: "#7c3aed" }} />}
        onClick={() => handleAddParty(true)}
      >
        Empresa
      </Button>
    </div>
  );

  return (
    <Col span={12} xl={12} xs={24}>
      {/* ── Panel header ───────────────────────────────── */}
      <div
        style={{
          background: light,
          border: `1px solid ${border}`,
          borderRadius: 10,
          padding: "10px 14px 10px 16px",
          marginBottom: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: accent, letterSpacing: 0.3 }}>
            {label}{parties.length > 1 ? "s" : ""}
          </span>
          {parties.length > 1 && (
            <Badge
              count={parties.length}
              style={{ background: accent, fontSize: 11, lineHeight: "18px", height: 18, minWidth: 18 }}
            />
          )}
        </div>

        <Popover
          content={addContent}
          trigger="click"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          placement="bottomRight"
        >
          <Button
            size="small"
            type="text"
            icon={<PlusOutlined />}
            style={{
              color: accent,
              fontWeight: 600,
              fontSize: 12,
              border: `1px dashed ${accent}88`,
              borderRadius: 6,
              padding: "2px 10px",
              height: 28,
            }}
          >
            Agregar {label.toLowerCase()}
          </Button>
        </Popover>
      </div>

      {/* ── Parties ────────────────────────────────────── */}
      {parties.map((person, index) => {
        const isCollapsed = !!collapsed[index];
        const showNumber = parties.length > 1;
        const partyLabel = showNumber ? `${label} #${index + 1}` : label;

        return (
          <div key={index} style={{ marginBottom: 10 }}>
            <PartyRow
              person={person}
              index={index}
              label={partyLabel}
              role={role}
              color={role === "sellers" ? "orange" : "blue"}
              accentColor={accent}
              isCollapsed={isCollapsed}
              onToggle={() => toggleCollapse(index)}
              onRemove={() => handleRemove(index)}
              canRemove={parties.length > 1}
            />

            {!isCollapsed && (
              <PersonForm
                role={role === "sellers" ? "seller" : "buyer"}
                partyRole={role}
                partyIndex={index}
                label={partyLabel}
                person={person}
                cartransferId={cartransferId}
                accentColor={accent}
              />
            )}
          </div>
        );
      })}
    </Col>
  );
};

const PersonStep = () => (
  <Row gutter={[20, 16]}>
    <PartyColumn role="sellers" />
    <PartyColumn role="buyers" />
  </Row>
);

export default PersonStep;
